import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import {
  FileText, RefreshCw, Search, Download, ExternalLink,
  Image, FileSpreadsheet, File, CheckCircle, Clock, AlertTriangle,
  HardDrive, ChevronDown, ChevronRight, Shield, Filter,
  TrendingUp, Package,
} from 'lucide-react';
import { companiesApi } from '../api';
import PageInfoButton from '@/components/PageInfoButton';

ChartJS.register(ArcElement, Tooltip, Legend);

/* ─── Types ─────────────────────────────────────────────────────────────── */

interface EvidenceItem {
  id: number;
  compliance_page_id: number;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  notes?: string;
  status: 'draft' | 'submitted';
  created_at: string;
  submitted_at?: string;
  uploader_name: string;
  page_title: string;
  framework_name?: string;
}

type FilterStatus = 'all' | 'submitted' | 'draft';
type SortKey = 'date' | 'name' | 'size';
type ViewMode = 'grouped' | 'flat';

/* ─── Helpers ───────────────────────────────────────────────────────────── */

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
}

function fileExt(name: string) {
  return name.split('.').pop()?.toUpperCase() || 'FILE';
}

function extColor(mime: string, type: string) {
  if (type === 'image' || mime.startsWith('image/'))
    return { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' };
  if (mime.includes('pdf'))
    return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' };
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' };
  if (mime.includes('word') || mime.includes('document'))
    return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' };
  if (mime.includes('zip') || mime.includes('compressed'))
    return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' };
  return { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' };
}

function FileTypeIcon({ mime, type, size = 'md' }: { mime: string; type: string; size?: 'sm' | 'md' }) {
  const sz = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  if (type === 'image' || mime.startsWith('image/')) return <Image className={`${sz} text-purple-600`} />;
  if (mime.includes('pdf')) return <FileText className={`${sz} text-red-600`} />;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return <FileSpreadsheet className={`${sz} text-emerald-600`} />;
  return <File className={`${sz} text-slate-500`} />;
}

function UploaderAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const colors = [
    'bg-indigo-500', 'bg-violet-500', 'bg-sky-500',
    'bg-emerald-500', 'bg-amber-500', 'bg-rose-500',
  ];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[9px] font-bold flex-shrink-0 ${color}`}>
      {initials || '?'}
    </span>
  );
}

/* ─── Doughnut chart ────────────────────────────────────────────────────── */

const doughnutOpts = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '72%',
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx: any) => ` ${ctx.label}: ${ctx.parsed}`,
      },
    },
  },
} as const;

/* ─── Row component ─────────────────────────────────────────────────────── */

const EvidenceRow: React.FC<{ item: EvidenceItem; showFramework?: boolean }> = ({ item, showFramework = false }) => {
  const colors = extColor(item.mime_type, item.file_type);
  return (
    <div className="group flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50/80 transition-colors border-b border-slate-100 last:border-0">

      {/* File icon box */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg border flex flex-col items-center justify-center gap-0.5 ${colors.bg} ${colors.border}`}>
        <FileTypeIcon mime={item.mime_type} type={item.file_type} size="sm" />
        <span className={`text-[8px] font-bold leading-none ${colors.text}`}>
          {fileExt(item.file_name)}
        </span>
      </div>

      {/* Name + page breadcrumb */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate leading-tight">{item.file_name}</p>
        <p className="text-xs text-slate-400 truncate mt-0.5">
          {showFramework && item.framework_name && (
            <>
              <span className="text-indigo-500 font-medium">{item.framework_name}</span>
              <span className="mx-1 text-slate-300">/</span>
            </>
          )}
          {item.page_title}
        </p>
      </div>

      {/* Uploader */}
      <div className="hidden md:flex items-center gap-1.5 w-32 flex-shrink-0">
        <UploaderAvatar name={item.uploader_name} />
        <span className="text-xs text-slate-500 truncate">{item.uploader_name.split(' ')[0]}</span>
      </div>

      {/* Size */}
      <div className="hidden lg:block w-16 flex-shrink-0 text-right">
        <span className="text-xs text-slate-400">{formatFileSize(item.file_size)}</span>
      </div>

      {/* Date */}
      <div className="hidden sm:block w-28 flex-shrink-0 text-right">
        <span className="text-xs text-slate-400">
          {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      </div>

      {/* Status badge */}
      <div className="flex-shrink-0">
        {item.status === 'INTERNAL_VERIFIED' ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
            <CheckCircle className="w-3 h-3" /> Verified
          </span>
        ) : item.status === 'REJECTED' ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full">
            <AlertTriangle className="w-3 h-3" /> Rejected
          </span>
        ) : item.status === 'submitted' ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
            <Clock className="w-3 h-3" /> Submitted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
            <Clock className="w-3 h-3" /> Draft
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <a
          href={item.file_url}
          target="_blank"
          rel="noopener noreferrer"
          title="View"
          className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <a
          href={item.file_url}
          download={item.file_name}
          title="Download"
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
};

/* ─── Main component ────────────────────────────────────────────────────── */

const CompanyEvidenceLibrary: React.FC = () => {
  const [allEvidence, setAllEvidence] = useState<EvidenceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [sort, setSort] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => { load(); }, []);

  const handleExportEvidenceCSV = () => {
    const csv = [
      ['File Name', 'Framework', 'Page Title', 'Status', 'File Type', 'Size (KB)', 'Uploaded By', 'Uploaded Date', 'Submitted Date'].join(','),
      ...allEvidence.map((e: any) => [
        `"${(e.file_name || '').replace(/"/g, '""')}"`,
        `"${(e.framework_name || '').replace(/"/g, '""')}"`,
        `"${(e.page_title || '').replace(/"/g, '""')}"`,
        e.status,
        e.mime_type || '',
        e.file_size ? (e.file_size / 1024).toFixed(1) : '',
        `"${(e.uploader_name || '').replace(/"/g, '""')}"`,
        e.created_at ? new Date(e.created_at).toLocaleDateString() : '',
        e.submitted_at ? new Date(e.submitted_at).toLocaleDateString() : '',
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence-library-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await companiesApi.getCompanyEvidence();
      const items: EvidenceItem[] = data.map((item: any) => ({
        id: item.id,
        compliance_page_id: item.compliance_page_id,
        file_name: item.file_name,
        file_url: item.file_url,
        file_type: item.file_type,
        file_size: item.file_size,
        mime_type: item.mime_type,
        notes: item.notes,
        status: item.status,
        created_at: item.created_at,
        submitted_at: item.submitted_at,
        uploader_name: item.first_name ? `${item.first_name} ${item.last_name}` : (item.uploader_name || 'Unknown'),
        page_title: item.page_title || 'Unknown Page',
        framework_name: item.framework_name,
      }));
      setAllEvidence(items);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const toggleCollapse = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  /* Derived stats (always from full dataset) */
  const totalSubmitted = allEvidence.filter(e => e.status === 'submitted').length;
  const totalDraft = allEvidence.filter(e => e.status === 'draft').length;
  const totalSize = allEvidence.reduce((s, e) => s + (e.file_size || 0), 0);
  const uniqueFrameworks = new Set(allEvidence.map(e => e.framework_name || 'Unassigned')).size;
  const submittedPct = allEvidence.length > 0 ? Math.round((totalSubmitted / allEvidence.length) * 100) : 0;

  const doughnutData = {
    labels: ['Submitted', 'Draft'],
    datasets: [{
      data: [totalSubmitted, totalDraft],
      backgroundColor: ['#10b981', '#f59e0b'],
      borderWidth: 0,
    }],
  };

  /* MIME type breakdown for second chart */
  const mimeGroups = useMemo(() => {
    const groups: Record<string, number> = {};
    allEvidence.forEach(e => {
      let label = 'Other';
      if (e.file_type === 'image' || e.mime_type.startsWith('image/')) label = 'Images';
      else if (e.mime_type.includes('pdf')) label = 'PDFs';
      else if (e.mime_type.includes('spreadsheet') || e.mime_type.includes('excel') || e.mime_type.includes('csv')) label = 'Spreadsheets';
      else if (e.mime_type.includes('word') || e.mime_type.includes('document')) label = 'Documents';
      groups[label] = (groups[label] || 0) + 1;
    });
    return groups;
  }, [allEvidence]);

  const typeDoughnutData = {
    labels: Object.keys(mimeGroups),
    datasets: [{
      data: Object.values(mimeGroups),
      backgroundColor: ['#8b5cf6', '#ef4444', '#10b981', '#3b82f6', '#94a3b8'],
      borderWidth: 0,
    }],
  };

  /* Filtered + sorted list */
  const filtered = useMemo(() => {
    let list = allEvidence.filter(e => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          e.file_name.toLowerCase().includes(q) ||
          e.page_title.toLowerCase().includes(q) ||
          (e.framework_name || '').toLowerCase().includes(q) ||
          e.uploader_name.toLowerCase().includes(q)
        );
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sort === 'date') cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      else if (sort === 'name') cmp = a.file_name.localeCompare(b.file_name);
      else if (sort === 'size') cmp = a.file_size - b.file_size;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [allEvidence, search, filterStatus, sort, sortDir]);

  /* Group by framework */
  const grouped = useMemo(() => {
    const map = new Map<string, EvidenceItem[]>();
    filtered.forEach(e => {
      const key = e.framework_name || 'No Framework';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [filtered]);

  /* ── Loading ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">

      {/* ── Hero Header ────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 leading-tight">Evidence Library</h1><PageInfoButton /></div>
                <p className="text-sm text-slate-500 mt-0.5">
                  {allEvidence.length} files · {uniqueFrameworks} framework{uniqueFrameworks !== 1 ? 's' : ''} · {submittedPct}% submitted
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportEvidenceCSV}
                disabled={allEvidence.length === 0}
                className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button
                onClick={load}
                className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
          </div>

          {/* ── Stats + Charts Row ─────────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">

            {/* Stat cards */}
            {[
              {
                label: 'Total Files',
                value: allEvidence.length,
                sub: `across ${uniqueFrameworks} frameworks`,
                icon: <FileText className="w-5 h-5 text-indigo-500" />,
                bg: 'from-indigo-50 to-violet-50',
                border: 'border-indigo-100',
              },
              {
                label: 'Submitted',
                value: totalSubmitted,
                sub: `${submittedPct}% of all files`,
                icon: <CheckCircle className="w-5 h-5 text-emerald-500" />,
                bg: 'from-emerald-50 to-teal-50',
                border: 'border-emerald-100',
              },
              {
                label: 'Draft',
                value: totalDraft,
                sub: `${100 - submittedPct}% pending submission`,
                icon: <Clock className="w-5 h-5 text-amber-500" />,
                bg: 'from-amber-50 to-orange-50',
                border: 'border-amber-100',
              },
              {
                label: 'Total Storage',
                value: formatFileSize(totalSize),
                sub: 'across all uploads',
                icon: <HardDrive className="w-5 h-5 text-slate-400" />,
                bg: 'from-slate-50 to-slate-100',
                border: 'border-slate-200',
              },
            ].map(s => (
              <div key={s.label} className={`rounded-xl border ${s.bg} ${s.border} p-4`}>
                <div className="flex items-center gap-2 mb-2">{s.icon}<span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{s.label}</span></div>
                <div className="text-3xl font-bold text-slate-900 leading-none">{s.value}</div>
                <div className="text-xs text-slate-400 mt-1">{s.sub}</div>
              </div>
            ))}

            {/* Mini charts side by side in last cell */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 col-span-2 lg:col-span-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Breakdown</p>
              <div className="flex items-center gap-3">
                {/* Status donut */}
                <div className="relative w-16 h-16 flex-shrink-0">
                  {allEvidence.length > 0
                    ? <Doughnut data={doughnutData} options={doughnutOpts} />
                    : <div className="w-16 h-16 rounded-full bg-slate-100" />
                  }
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-slate-700">{submittedPct}%</span>
                  </div>
                </div>
                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="text-[11px] text-slate-600 leading-none">Submitted</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                    <span className="text-[11px] text-slate-600 leading-none">Draft</span>
                  </div>
                  {Object.keys(mimeGroups).length > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t border-slate-100 text-[10px] text-slate-400">
                      {Object.entries(mimeGroups).map(([type, cnt]) => (
                        <div key={type} className="flex justify-between">
                          <span>{type}</span><span className="font-semibold text-slate-600">{cnt}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Content ────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 py-5 space-y-4">

        {/* ── Toolbar ──────────────────────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3 flex flex-wrap gap-3 items-center shadow-sm">

          {/* Search */}
          <div className="relative flex-1 min-w-52">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search files, pages, uploaders…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-colors"
            />
          </div>

          {/* Status filter */}
          <div className="flex gap-0.5 p-0.5 bg-slate-100 rounded-xl">
            {([
              { key: 'all', label: 'All', count: allEvidence.length },
              { key: 'submitted', label: 'Submitted', count: totalSubmitted },
              { key: 'draft', label: 'Draft', count: totalDraft },
            ] as { key: FilterStatus; label: string; count: number }[]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilterStatus(tab.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  filterStatus === tab.key
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  filterStatus === tab.key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Filter className="w-3.5 h-3.5" />
            {([
              { key: 'date', label: 'Date' },
              { key: 'name', label: 'Name' },
              { key: 'size', label: 'Size' },
            ] as { key: SortKey; label: string }[]).map(opt => (
              <button
                key={opt.key}
                onClick={() => {
                  if (sort === opt.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                  else { setSort(opt.key); setSortDir('desc'); }
                }}
                className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-all ${
                  sort === opt.key
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {opt.label}{sort === opt.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
            ))}
          </div>

          {/* View mode */}
          <div className="flex gap-0.5 p-0.5 bg-slate-100 rounded-xl ml-auto">
            {([
              { key: 'grouped', icon: <Shield className="w-3.5 h-3.5" />, label: 'Grouped' },
              { key: 'flat', icon: <TrendingUp className="w-3.5 h-3.5" />, label: 'Flat' },
            ] as { key: ViewMode; icon: React.ReactNode; label: string }[]).map(v => (
              <button
                key={v.key}
                onClick={() => setViewMode(v.key)}
                title={v.label}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  viewMode === v.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {v.icon} {v.label}
              </button>
            ))}
          </div>

          <span className="text-xs text-slate-400">{filtered.length} file{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* ── Column headers ───────────────────────────────────────────────── */}
        {filtered.length > 0 && (
          <div className="hidden md:flex items-center gap-4 px-5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <div className="w-9 flex-shrink-0" />
            <div className="flex-1">File</div>
            <div className="w-32 flex-shrink-0 hidden md:block">Uploaded by</div>
            <div className="w-16 flex-shrink-0 hidden lg:block text-right">Size</div>
            <div className="w-28 flex-shrink-0 hidden sm:block text-right">Date</div>
            <div className="w-24 flex-shrink-0">Status</div>
            <div className="w-16 flex-shrink-0" />
          </div>
        )}

        {/* ── Empty state ──────────────────────────────────────────────────── */}
        {filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl py-20 text-center shadow-sm">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="font-semibold text-slate-700 text-base">
              {search || filterStatus !== 'all' ? 'No files match your filters' : 'No evidence uploaded yet'}
            </h3>
            <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
              {search || filterStatus !== 'all'
                ? 'Try adjusting your search or status filter.'
                : 'Evidence uploaded to compliance pages will appear here for central review.'}
            </p>
          </div>
        )}

        {/* ── Grouped view ─────────────────────────────────────────────────── */}
        {filtered.length > 0 && viewMode === 'grouped' && grouped.map(([framework, items]) => {
          const isCollapsed = collapsed.has(framework);
          const submitted = items.filter(e => e.status === 'submitted').length;
          const pct = items.length > 0 ? Math.round((submitted / items.length) * 100) : 0;

          return (
            <div key={framework} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              {/* Group header */}
              <button
                onClick={() => toggleCollapse(framework)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left border-b border-slate-100"
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-800">{framework}</span>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium">
                      {items.length} file{items.length !== 1 ? 's' : ''}
                    </span>
                    {submitted > 0 && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                        {submitted} submitted
                      </span>
                    )}
                  </div>
                  {/* Mini progress bar */}
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden max-w-xs">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-slate-400 font-medium">{pct}% submitted</span>
                  </div>
                </div>
                {isCollapsed
                  ? <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                }
              </button>

              {/* Group rows */}
              {!isCollapsed && (
                <div>
                  {items.map(item => (
                    <EvidenceRow key={item.id} item={item} showFramework={false} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Flat view ────────────────────────────────────────────────────── */}
        {filtered.length > 0 && viewMode === 'flat' && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            {filtered.map(item => (
              <EvidenceRow key={item.id} item={item} showFramework />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyEvidenceLibrary;
